<template>
  <div class="chart">
    <Empty
      v-if="isEmpty"
      class="empty"
      tips="没有数据"
    />
    <div
      ref="chart"
      style="height: 500px"
    />
  </div>
</template>

<script>
import echart from 'echarts';

import { grid, legendStyle, xAxisStyle } from '@/config/chart/line';
import * as pieStyle from '@/config/chart/pie';
import { getField } from '@/config/columns/';
import { getMaxIntFromNumbers } from '@/common/chart';
import { fenToYuan } from '@/common/number/';
import accounting from 'accounting';
import Empty from '@/components/Empty';

export default {
  components: {
    Empty,
  },

  props: {
    dataSource: {
      type: [Object, Array],
      required: true,
    },

    type: {
      type: String,
      default: '1',
    },

    lineType: {
      type: String,
      default: '1',
    },
  },

  data() {
    return {
      temp: '',
    };
  },

  computed: {
    isEmpty() {
      if (this.type === '1' && this.lineType === '2') {
        return !this.dataSource;
      } else {
        return !(this.data && this.data.length > 0);
      }
    },

    data() {
      return this.dataSource.data;
    },

    compare() {
      return this.dataSource.compare_data;
    },

    isDoubleX() {
      const field1 = this.getField(this.data);
      const field2 = this.getField(this.compare);

      if (field1 === field2) {
        return true;
      }

      return false;
    },

    isDoubleY() {
      return this.compare && !this.isDoubleX;
    },
  },

  watch: {
    dataSource() {
      if (this.type === '1') {
        if (this.lineType === '1') {
          this.render();
        } else if (this.lineType === '2') {
          this.renderTop();
        }
      } else if (this.type === '2') {
        this.renderPie();
      }
    },
  },

  mounted() {
    this.chart = echart.init(this.$refs.chart);
  },

  methods: {
    render() {
      const option = {
        legend: {
          ...legendStyle,
          data: [],
        },
        grid,
        tooltip: {
          trigger: 'axis',
        },
        xAxis: [],
        yAxis: [],
        series: [],
      };

      if (this.data) {
        let legendData = [];
        const xAxisData = [];
        const seriesData = [];
        this.data.forEach((item) => {
          const key = item.field;
          legendData.push(getField(key).name);

          xAxisData.push(item.from_date);

          const value =
            key.indexOf('cost') !== -1 ? fenToYuan(item[key]) : item[key];
          seriesData.push(value);
        });

        if (this.isDoubleX) {
          legendData = [`${xAxisData[0]} ~ ${xAxisData[xAxisData.length - 1]}`];
        }

        option.legend.data = [...option.legend.data, ...legendData];

        option.xAxis.push({
          ...xAxisStyle,
          data: xAxisData,
        });

        option.yAxis.push({
          ...this.getYAxis(seriesData),
          type: 'value',
          axisLabel: {
            formatter: '{value}',
          },
        });

        option.series.push({
          yAxisIndex: 0,
          type: 'line',
          name: legendData[0],
          data: seriesData,
        });
      }

      if (this.compare) {
        let legendData = [];
        const xAxisData = [];
        const seriesData = [];
        this.compare.forEach((item) => {
          const key = item.field;
          legendData.push(getField(key).name);

          xAxisData.push(item.from_date);

          const value =
            key.indexOf('cost') !== -1 ? fenToYuan(item[key]) : item[key];
          seriesData.push(value);
        });

        if (this.isDoubleX) {
          legendData = [`${xAxisData[0]} ~ ${xAxisData[xAxisData.length - 1]}`];
        }

        option.legend.data = [...option.legend.data, ...legendData];

        if (this.isDoubleX) {
          option.xAxis.push({
            ...xAxisStyle,
            position: 'bottom',
            offset: 30,
            axisTick: {
              show: false,
            },
            axisLine: {
              show: false,
            },
            data: xAxisData,
          });
        }

        option.yAxis.push({
          ...this.getYAxis(seriesData),
          show: this.isDoubleY,
          type: 'value',
          axisLabel: {
            formatter: '{value}',
          },
        });

        option.series.push({
          yAxisIndex: 1,
          type: 'line',
          name: legendData[0],
          data: seriesData,
        });
      }

      console.dir(option);

      this.chart.setOption(option, true);
    },

    renderTop() {
      const option = {
        legend: {
          ...legendStyle,
          data: [],
        },
        grid,
        tooltip: {
          trigger: 'axis',
        },
        xAxis: [],
        yAxis: [],
        series: [],
      };

      if (this.dataSource) {
        let legendData = [];
        let xAxisData = [];
        let max = [];

        Object.keys(this.dataSource).forEach((item) => {
          const data = this.dataSource[item];
          const list = data.list;

          legendData.push(data.name);

          xAxisData = list.map((item) => item.from_date);

          const seriesData = list.map((it) => {
            const key = it.field;
            const value =
              key.indexOf('cost') !== -1
                ? fenToYuan(it[it.field])
                : it[it.field];
            return value;
          });

          max.push(Math.max.apply(null, seriesData));

          option.series.push({
            type: 'line',
            name: data.name,
            data: seriesData,
          });
        });

        option.legend.data = legendData;

        option.xAxis.push({
          ...xAxisStyle,
          data: xAxisData,
        });

        option.yAxis.push({
          ...this.getYAxis(max),
          type: 'value',
          axisLabel: {
            formatter: '{value}',
          },
        });

        this.chart.setOption(option, true);
      }
    },

    renderPie() {
      const option = {
        legend: {
          ...pieStyle.legendStyle,
          data: [],
        },
        grid,
        tooltip: pieStyle.tooltipStyle,
        series: [],
      };

      if (this.isDoubleX) {
        const a = this.data[0];
        const b = this.compare ? this.compare[0] : null;
        if (a && b) {
          option.title = {
            text: `${a.from_date} ~ ${a.end_date}                                               ${b.from_date} ~ ${b.end_date} `,
            top: 70,
            left: 120,
          };
        }
      }

      if (this.data) {
        let total = 0;
        let name1;
        let name2;
        let fie;
        const seriesData = [];

        this.data.forEach((item) => {
          const dim = item.dimension;
          fie = item.field;
          name1 = item[dim];
          name2 = getField(fie).name;

          option.legend.data.push(name1);

          const value =
            fie.indexOf('cost') !== -1
              ? fenToYuan(item[fie])
              : parseInt(item[fie], 10);

          total = total + value;

          seriesData.push({
            name: name1,
            value,
          });
        });

        const series = {
          ...pieStyle.seriesStyle,
          label: {
            normal: {
              show: true,
              position: 'center',
              textStyle: {
                fontSize: '20',
                fontWeight: 'bold',
              },
              formatter() {
                const isMoney = fie.indexOf('cost') !== -1;
                return `${name2}\n\n${
                  isMoney
                    ? accounting.formatMoney(total, '￥')
                    : accounting.formatNumber(total)
                }`;
              },
            },
            emphasis: {
              show: true,
              textStyle: {
                fontSize: '20',
                fontWeight: 'bold',
              },
            },
          },
          name: name2,
          data: seriesData,
        };

        option.series.push(series);
      }

      if (this.compare) {
        let total = 0;
        let name1;
        let name2;
        let fie;
        const seriesData = [];

        this.compare.forEach((item) => {
          const dim = item.dimension;
          fie = item.field;
          name1 = item[dim];
          name2 = getField(fie).name;

          option.legend.data.push(name1);

          const value =
            fie.indexOf('cost') !== -1
              ? fenToYuan(item[fie])
              : parseInt(item[fie], 10);

          total += value;

          seriesData.push({
            name: name1,
            value,
          });
        });

        const series = {
          ...pieStyle.seriesStyle,
          radius: ['30%', '50%'],
          center: ['60%', '50%'],
          name: name2,
          data: seriesData,
          label: {
            normal: {
              show: true,
              position: 'center',
              textStyle: {
                fontSize: '14',
                fontWeight: 'bold',
              },
              formatter() {
                const isMoney = fie.indexOf('cost') !== -1;
                return `${name2}\n\n${
                  isMoney
                    ? accounting.formatMoney(total, '￥')
                    : accounting.formatNumber(total)
                }`;
              },
            },
            emphasis: {
              show: true,
              textStyle: {
                fontSize: '14',
                fontWeight: 'bold',
              },
            },
          },
        };

        option.series.push(series);

        option.series[0].radius = ['30%', '50%'];
        option.series[0].center = ['20%', '50%'];
        option.series[0].label.normal.textStyle.fontSize = '14';
        option.series[0].label.emphasis.textStyle.fontSize = '14';
      }

      this.chart.setOption(option, true);
    },

    getXAxisData(data) {
      if (data) {
        return data.map((item) => item.from_date);
      }
      return [];
    },

    getField(data) {
      if (data && data[0]) {
        const { field } = data[0];
        if (field) {
          return getField(field).name;
        }
      }
      return '';
    },

    getSeries(data) {
      const that = this;
      if (data) {
        return data.map((item) => {
          const key = item.field;
          that.temp = key;
          if (key.indexOf('cost') !== -1) {
            return fenToYuan(item[key]);
          }
          return item[key];
        });
      }
      return [];
    },

    getYAxis(series) {
      const min = 0;
      const max = getMaxIntFromNumbers(series);
      const splitNumber = 5;

      return {
        min,
        max,
        splitNumber: 5,
        interval: (max - min) / splitNumber,
      };
    },
  },
};
</script>

<style lang="less" scoped>
.chart {
  position: relative;
}

.empty {
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  height: 370px;
  background: #fff;
  z-index: 10;
}
</style>